<template>
  <div
    class="collapsible-split-panel"
    :class="{
      'is-collapsed': isLeftCollapsed || isRightCollapsed,
      'is-switch-mode': isSwitchMode
    }"
    :style="panelStyle"
  >
    <section
      class="panel panel--left"
      :class="{ 'panel--left-collapsed': isLeftCollapsed }"
      :style="leftStyle"
    >
      <div class="panel__header" :class="{ 'panel__header--collapsed': isLeftCollapsed }">
        <span v-if="showLeftTitle" class="panel__title">
          <slot name="left-title">{{ leftTitle }}</slot>
        </span>
        <button
          v-if="showLeftToggle"
          :aria-label="leftToggleAriaLabel"
          class="panel__toggle"
          :class="[isLeftCollapsed ? 'panel__toggle--expand-left' : 'panel__toggle--collapse-left']"
          type="button"
          @click="toggleLeft"
        >
          <i
            class="t-iconfont"
            :class="[
              isLeftCollapsed ? 'icon-arrow-right-double-line' : 'icon-arrow-left-double-line'
            ]"
          ></i>
        </button>
      </div>

      <div class="panel__content">
        <div class="panel__body panel__body--left" :class="{ 'is-hidden': isLeftCollapsed }">
          <slot :collapsed="isLeftCollapsed" name="left"></slot>
        </div>

        <div class="panel__collapsed-body" :class="{ 'is-visible': isLeftCollapsed }">
          <template v-if="leftCollapsedTextList.length">
            <span v-for="(item, index) in leftCollapsedTextList" :key="`left-${index}-${item}`">{{
              item
            }}</span>
          </template>
        </div>
      </div>
    </section>

    <section
      class="panel panel--right"
      :class="{ 'panel--right-collapsed': isRightCollapsed }"
      :style="rightStyle"
    >
      <div class="panel__header" :class="{ 'panel__header--collapsed': isRightCollapsed }">
        <span v-if="showRightTitle" class="panel__title">
          <slot name="right-title">{{ rightTitle }}</slot>
        </span>
        <button
          v-if="showRightToggle"
          :aria-label="rightToggleAriaLabel"
          class="panel__toggle"
          :class="[
            isRightCollapsed ? 'panel__toggle--expand-right' : 'panel__toggle--collapse-right'
          ]"
          type="button"
          @click="toggleRight"
        >
          <i
            class="t-iconfont"
            :class="[
              isRightCollapsed ? 'icon-arrow-left-double-line' : 'icon-arrow-right-double-line'
            ]"
          ></i>
        </button>
      </div>
      <div class="panel__content">
        <div class="panel__body panel__body--right" :class="{ 'is-hidden': isRightCollapsed }">
          <slot :collapsed="isRightCollapsed" name="right"></slot>
        </div>

        <div class="panel__collapsed-body" :class="{ 'is-visible': isRightCollapsed }">
          <template v-if="rightCollapsedTextList.length">
            <span v-for="(item, index) in rightCollapsedTextList" :key="`right-${index}-${item}`">{{
              item
            }}</span>
          </template>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false
  },
  leftTitle: {
    type: String,
    default: '左侧内容'
  },
  rightTitle: {
    type: String,
    default: '右侧内容'
  },
  collapseAriaLabel: {
    type: String,
    default: '收起左侧内容'
  },
  expandAriaLabel: {
    type: String,
    default: '展开左侧内容'
  },
  collapsedText: {
    type: [String, Array],
    default: () => ['展', '开', '查', '看', '内', '容']
  },
  leftCollapsedText: {
    type: [String, Array],
    default: ''
  },
  rightCollapsedText: {
    type: [String, Array],
    default: ''
  },
  leftWidthPercent: {
    type: Number,
    default: 40.12
  },
  collapsedWidth: {
    type: Number,
    default: 52
  },
  gap: {
    type: Number,
    default: 12
  },
  showToggle: {
    type: Boolean,
    default: true
  },
  mode: {
    type: String,
    default: 'split' // split switch
  },
  collapseSide: {
    type: String,
    default: 'left' // left right，仅 split 模式生效
  },
  beforeToggle: {
    type: Function,
    default: null
  }
})

const emit = defineEmits(['update:collapsed', 'toggle-collapse'])

const isSwitchMode = computed(() => props.mode === 'switch')
const splitCollapseSide = computed(() => (props.collapseSide === 'right' ? 'right' : 'left'))

const panelState = ref({
  collapsed: Boolean(props.collapsed)
})

watch(
  () => props.collapsed,
  (value) => {
    panelState.value = {
      collapsed: Boolean(value)
    }
  }
)

const panelStyle = computed(() => ({
  '--panel-gap': `${Math.max(0, Number(props.gap) || 0)}px`
}))

const isLeftCollapsed = computed(() =>
  isSwitchMode.value
    ? panelState.value.collapsed
    : splitCollapseSide.value === 'left' && panelState.value.collapsed
)
const isRightCollapsed = computed(() =>
  isSwitchMode.value
    ? !panelState.value.collapsed
    : splitCollapseSide.value === 'right' && panelState.value.collapsed
)

const showLeftTitle = computed(() => !isLeftCollapsed.value)
const showRightTitle = computed(() => !isRightCollapsed.value)
const showLeftToggle = computed(
  () =>
    props.showToggle &&
    (isSwitchMode.value ? isLeftCollapsed.value : splitCollapseSide.value === 'left')
)
const showRightToggle = computed(
  () =>
    props.showToggle &&
    (isSwitchMode.value ? isRightCollapsed.value : splitCollapseSide.value === 'right')
)

const leftToggleAriaLabel = computed(() =>
  isLeftCollapsed.value ? `展开${props.leftTitle}` : `收起${props.leftTitle}`
)
const rightToggleAriaLabel = computed(() =>
  isRightCollapsed.value ? `展开${props.rightTitle}` : `收起${props.rightTitle}`
)

const getCollapsedTextList = (value, fallbackValue = []) => {
  const targetValue = value === '' || value == null ? fallbackValue : value

  if (Array.isArray(targetValue)) return targetValue.map((item) => String(item))
  return String(targetValue).split('')
}

const leftCollapsedTextList = computed(() =>
  getCollapsedTextList(props.leftCollapsedText, props.collapsedText)
)

const rightCollapsedTextList = computed(() =>
  isSwitchMode.value
    ? getCollapsedTextList(props.rightCollapsedText, props.rightTitle)
    : getCollapsedTextList(props.rightCollapsedText, props.collapsedText)
)

const leftStyle = computed(() => {
  const collapsedWidth = Math.max(0, Number(props.collapsedWidth) || 0)
  const gap = Math.max(0, Number(props.gap) || 0)
  const leftWidthPercent = Math.min(100, Math.max(0, Number(props.leftWidthPercent) || 0))

  if (isSwitchMode.value) {
    if (isLeftCollapsed.value) {
      return {
        width: `${collapsedWidth}px`,
        flex: '0 0 auto'
      }
    }

    return {
      width: `calc(100% - ${collapsedWidth + gap}px)`,
      flex: '0 0 auto'
    }
  }

  if (isLeftCollapsed.value) {
    return {
      width: `${collapsedWidth}px`,
      flex: '0 0 auto'
    }
  }

  if (isRightCollapsed.value) {
    return {
      flex: '1 1 auto'
    }
  }

  return {
    width: `calc((100% - ${gap}px) * ${leftWidthPercent / 100})`
  }
})

const rightStyle = computed(() => {
  const collapsedWidth = Math.max(0, Number(props.collapsedWidth) || 0)
  const gap = Math.max(0, Number(props.gap) || 0)

  if (isSwitchMode.value) {
    if (isRightCollapsed.value) {
      return {
        width: `${collapsedWidth}px`,
        flex: '0 0 auto'
      }
    }

    return {
      width: `calc(100% - ${collapsedWidth + gap}px)`,
      flex: '0 0 auto'
    }
  }

  if (isRightCollapsed.value) {
    return {
      width: `${collapsedWidth}px`,
      flex: '0 0 auto'
    }
  }

  if (isLeftCollapsed.value) {
    return {
      flex: '1 1 auto'
    }
  }

  return {
    flex: '1 1 auto'
  }
})

const getTogglePayload = (collapsed) => ({
  mode: props.mode,
  collapsed,
  leftCollapsed: isSwitchMode.value ? collapsed : splitCollapseSide.value === 'left' && collapsed,
  rightCollapsed: isSwitchMode.value ? !collapsed : splitCollapseSide.value === 'right' && collapsed
})

const updateCollapsed = async (collapsed) => {
  const nextCollapsed = Boolean(collapsed)
  const togglePayload = getTogglePayload(nextCollapsed)

  if (typeof props.beforeToggle === 'function') {
    const shouldContinue = await props.beforeToggle(togglePayload)

    if (shouldContinue !== true) return
  }

  panelState.value = { collapsed: nextCollapsed }
  emit('update:collapsed', nextCollapsed)
  emit('toggle-collapse', togglePayload)
}

const toggleLeft = async () => {
  await updateCollapsed(!panelState.value.collapsed)
}

const toggleRight = async () => {
  if (!isSwitchMode.value && splitCollapseSide.value !== 'right') return
  await updateCollapsed(!panelState.value.collapsed)
}
</script>

<style lang="scss" scoped>
.collapsible-split-panel {
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
  gap: var(--panel-gap, 12px);

  .panel {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-width: 0;
    min-height: 0;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
    // box-shadow: 0px 2px 30px 2px rgba(0, 0, 0, 0.05);

    &--left {
      flex-shrink: 0;
      position: relative;
      transition: width 0.36s cubic-bezier(0.22, 1, 0.36, 1);
      will-change: width;
    }

    &--right {
      flex: 1;
      min-width: 0;
      contain: layout paint;
      transition: width 0.36s cubic-bezier(0.22, 1, 0.36, 1);
      will-change: width;
    }

    &__header {
      height: 46px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      box-sizing: border-box;
      border-bottom: 1px solid #e5e6eb;
      transition: padding 0.36s cubic-bezier(0.22, 1, 0.36, 1);

      &--collapsed {
        justify-content: center;
        padding: 12px 0;
      }
    }

    &__title {
      width: 100%;
      font-size: 16px;
      line-height: 22px;
      font-weight: 600;
      color: #323233;
    }

    &__toggle {
      width: 20px;
      height: 20px;
      border: 0;
      padding: 0;
      background: transparent;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      flex-shrink: 0;
      color: #323233;

      .t-iconfont {
        font-size: 20px;
        line-height: 1;
      }
    }

    &__content {
      position: relative;
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }

    &__collapsed-body {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      padding: 16px 0;
      color: #c8c9cc;
      font-size: 12px;
      line-height: 16px;
      text-align: center;
      opacity: 0;
      transform: translateY(6px);
      pointer-events: none;
      transition:
        opacity 0.16s ease 0s,
        transform 0.24s cubic-bezier(0.22, 1, 0.36, 1) 0s;

      &.is-visible {
        opacity: 1;
        transform: translateY(0);
        transition-delay: 0.18s;
      }

      span {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
      }
    }

    &__body {
      flex: 1;
      min-width: 0;
      min-height: 0;

      &--left {
        position: absolute;
        inset: 0;
        overflow: auto;
        scrollbar-width: none;
        -ms-overflow-style: none;
        opacity: 1;
        transform: translateX(0);
        transition:
          opacity 0.16s ease,
          transform 0.24s cubic-bezier(0.22, 1, 0.36, 1);

        &.is-hidden {
          opacity: 0;
          transform: translateX(-8px);
          pointer-events: none;
        }

        &::-webkit-scrollbar {
          display: none;
        }
      }

      &--right {
        position: absolute;
        inset: 0;
        overflow: hidden;
        opacity: 1;
        transform: translateX(0);
        transition:
          opacity 0.16s ease,
          transform 0.24s cubic-bezier(0.22, 1, 0.36, 1);

        &.is-hidden {
          opacity: 0;
          transform: translateX(8px);
          pointer-events: none;
        }
      }
    }
  }
}

@media (max-width: 1280px) {
  .collapsible-split-panel:not(.is-switch-mode) {
    .panel {
      &--left,
      &--right {
        width: auto !important;
        flex: 1;
      }

      &--left-collapsed,
      &--right-collapsed {
        width: 52px !important;
        flex: none;
      }
    }
  }
}
</style>
